import { FeatureFlagKey } from "@redotech/feature-flags-model";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { REDO_EFFECT_API_URL } from "@redotech/redo-merchant-app-common/config";
import {
  EffectApiClientContext,
  EffectApiClientProvider,
} from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import { createContext, memo, ReactNode, useContext, useMemo } from "react";
import { createGlobalConversationCollections } from "../db/collections";
import { GlobalConversationCollectionsContext } from "./global-conversation-collections-context";
import { MessageDbSyncProvider } from "./message-db-sync-provider";
import { NotificationStreamProvider } from "./notification-stream-provider";

const GlobalConversationStoreProvider = memo(
  function GlobalConversationStoreProvider({
    children,
  }: {
    children: ReactNode;
  }) {
    const effectApiClient = useRequiredContext(EffectApiClientContext);
    const collections = useMemo(() => {
      return createGlobalConversationCollections({
        queryClient: reactQueryClient,
        effectApi: effectApiClient,
      });
    }, [effectApiClient]);

    return (
      <GlobalConversationCollectionsContext.Provider value={collections}>
        {children}
      </GlobalConversationCollectionsContext.Provider>
    );
  },
);

interface SupportProvidersProps {
  children: ReactNode;
}

let cachedFeatureFlagEnabled: boolean | null = null;
let featureFlagPromise: Promise<boolean> | null = null;

const FeatureFlagEnabledContext = createContext<boolean | undefined>(undefined);
FeatureFlagEnabledContext.displayName = "FeatureFlagEnabledContext";

export function useFeatureFlagEnabled(): boolean {
  return useRequiredContext(FeatureFlagEnabledContext);
}

const FeatureFlagEnabledProvider = memo(function FeatureFlagEnabledProvider({
  children,
}: {
  children: ReactNode;
}) {
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );

  const featureFlagLoad = useLoad(async () => {
    if (cachedFeatureFlagEnabled !== null) {
      return cachedFeatureFlagEnabled;
    }

    if (featureFlagPromise === null) {
      featureFlagPromise = platformRpcClient
        .getFeatureFlag({ flagKey: FeatureFlagKey.SUPPORT_HELPDESK_V2 })
        .then((response) => {
          cachedFeatureFlagEnabled = response.enabled;
          return response.enabled;
        });
    }

    return featureFlagPromise;
  }, []);

  if (featureFlagLoad.pending) {
    return null;
  }

  return (
    <FeatureFlagEnabledContext.Provider value={featureFlagLoad.value ?? false}>
      {children}
    </FeatureFlagEnabledContext.Provider>
  );
});

export const SupportProviders = memo(function SupportProviders({
  children,
}: SupportProvidersProps) {
  const auth = useContext(AuthContext);

  return (
    <EffectApiClientProvider
      authToken={auth?.token}
      baseUrl={REDO_EFFECT_API_URL}
    >
      <GlobalConversationStoreProvider>
        <NotificationStreamProvider>
          <MessageDbSyncProvider>
            <FeatureFlagEnabledProvider>{children}</FeatureFlagEnabledProvider>
          </MessageDbSyncProvider>
        </NotificationStreamProvider>
      </GlobalConversationStoreProvider>
    </EffectApiClientProvider>
  );
});
